<div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 max-w-4xl w-full">
  <!-- Header -->
  @if (showHeader()) {
  <div class="flex items-center justify-between px-6 py-5 border-b border-slate-200 dark:border-slate-700">
    <div>
      <p class="text-xs font-semibold text-slate-400 mb-0.5">Loan Application</p>
      <h2 class="text-xl font-bold text-slate-900 dark:text-white">#{{ loanInfo().reference ? loanInfo().reference : (loanInfo().id ?
        loanInfo().id!.substring(0, 8).toUpperCase() : '—') }}</h2>
    </div>
    <div class="flex items-center gap-3">
      <span class="text-xxxs font-bold px-2.5 py-1 rounded-sm uppercase tracking-normal border"
        [class]="loanInfo().status | loanStatusStyle">
        {{ loanInfo().status | loanStatusLabel }}
      </span>
      @if (loanInfo().status === 'DISBURSED') {
      <button (click)="downloadDisbursementPDF()"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary-800 hover:bg-primary-700 rounded-lg transition-colors">
        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
        </svg>
        Download PDF
      </button>
      }
      <button (click)="onCloseClick()" class="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-900 transition-colors">
        <svg class="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  </div>
  }

  <!-- Content -->
  <div class="px-6 py-6 space-y-6 max-h-[calc(100vh-220px)] overflow-y-auto">

    <!-- Disbursement PDF Download (shown when header is hidden) -->
    @if (!showHeader() && loanInfo().status === 'DISBURSED') {
    <div class="flex items-center justify-between bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900 rounded-lg px-4 py-3">
      <div class="flex items-center gap-2">
        <svg class="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <p class="text-xs font-semibold text-emerald-700 dark:text-emerald-400">This loan has been disbursed.</p>
      </div>
      <div class="flex items-center gap-2">
        <button (click)="sendDisbursementEmail()" [disabled]="isSendingEmail"
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-primary-800 bg-white border border-primary-200 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          @if (isSendingEmail) {
            <svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            Sending...
          } @else {
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
            </svg>
            Send Email
          }
        </button>
        <button (click)="downloadDisbursementPDF()"
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary-800 hover:bg-primary-700 rounded-lg transition-colors">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          Download PDF
        </button>
      </div>
    </div>
    }

    <!-- Applicant Details -->
    <div>
      <div class="flex items-center gap-2 mb-3">
        <div class="w-1 h-4 bg-primary-800 rounded-full"></div>
        <h3 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">Applicant</h3>
      </div>
      <div class="bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/50 rounded-lg p-5">
        <div class="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          <div>
            <p class="text-xs font-semibold text-slate-400">Full Name</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">
              {{ loanApplicantInfo?.firstName || '—' }}
              @if (loanApplicantInfo?.otherName) { {{ loanApplicantInfo?.otherName }} }
              {{ loanApplicantInfo?.lastName || '' }}
            </p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Phone</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100 font-mono">{{ loanApplicantInfo?.mobileNumber || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Email</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100 break-all">{{ loanApplicantInfo?.email || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Monthly Income</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ monthlyIncome | number:'1.0-0' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Ghana Card</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100 font-mono">{{ loanInfo().userGhanaCard || loanApplicantInfo?.ghanaCardNumber || '—' }}</p>
            @if (loanApplicantInfo?.isGhanaCardVerified !== undefined) {
            <span class="inline-flex items-center gap-1 mt-0.5 text-[10px] font-bold uppercase"
              [ngClass]="loanApplicantInfo?.isGhanaCardVerified ? 'text-emerald-600' : 'text-amber-600'">
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                @if (loanApplicantInfo?.isGhanaCardVerified) {
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
                } @else {
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                }
              </svg>
              {{ loanApplicantInfo?.isGhanaCardVerified ? 'Verified' : 'Unverified' }}
            </span>
            }
          </div>
          @if (loanApplicantInfo?.gender) {
          <div>
            <p class="text-xs font-semibold text-slate-400">Gender</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanApplicantInfo?.gender }}</p>
          </div>
          }
          <div>
            <p class="text-xs font-semibold text-slate-400">Institution</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanInfo().institution?.name || loanInfo().institution || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Date Applied</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ (loanInfo().createdAt || loanInfo().created_at) | date:'dd MMM yyyy, HH:mm' }}</p>
          </div>
          @if (loanApplicantInfo?.kyc?.occupation) {
          <div>
            <p class="text-xs font-semibold text-slate-400">Occupation</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanApplicantInfo?.kyc?.occupation }}</p>
          </div>
          }
          @if (loanApplicantInfo?.account) {
          <div>
            <p class="text-xs font-semibold text-slate-400">Settlement Account</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100 font-mono">{{ loanApplicantInfo?.account?.accountNumber || '—' }}</p>
            <p class="text-[10px] text-slate-400 mt-0.5">{{ loanApplicantInfo?.account?.network || loanApplicantInfo?.account?.accountType }}</p>
          </div>
          }
        </div>
      </div>
    </div>

    <!-- Loan Details -->
    <div>
      <div class="flex items-center gap-2 mb-3">
        <div class="w-1 h-4 bg-primary-800 rounded-full"></div>
        <h3 class="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">Loan Details</h3>
      </div>
      <div class="bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/50 rounded-lg p-5">
        <div class="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
          @if (loanInfo().reference) {
          <div>
            <p class="text-xs font-semibold text-slate-400">Loan Reference</p>
            <p class="mt-1 text-sm font-bold text-primary-800 dark:text-primary-400 font-mono">{{ loanInfo().reference }}</p>
          </div>
          }
          <div>
            <p class="text-xs font-semibold text-slate-400">Product</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanInfo().product?.name || '—' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Principal Amount</p>
            <p class="mt-1 text-lg font-bold text-primary-800 dark:text-primary-400">GHS {{ (loanInfo().principal_amount || loanInfo().amount) | number:'1.2-2' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Receivable Amount</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().receivableAmount || 0) | number:'1.2-2' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Loan Term</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanInfo().tenure || '—' }} {{ getTenureUnit(loanInfo().product?.tenureType, loanInfo().tenure) }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Total Interest</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().interest || 0) | number:'1.2-2' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Total Repayment</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().totalRepayment || 0) | number:'1.2-2' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">{{ getRepaymentLabel(loanInfo().product?.tenureType) }}</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().monthlyRepayment || 0) | number:'1.2-2' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Interest Rate</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanInfo().interestRate || '—' }}%</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Processing Fee</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().processingFee || loanInfo().product?.processingFee || 0) | number:'1.2-2' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Processing Fee Rate</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanInfo().processingFeeRate || '—' }}%</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Total Paid</p>
            <p class="mt-1 text-sm font-bold text-emerald-600">GHS {{ (loanInfo().totalAmountPaid || 0) | number:'1.2-2' }}</p>
          </div>
          <div>
            <p class="text-xs font-semibold text-slate-400">Repayment Date</p>
            <p class="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanInfo().repaymentDate | date:'dd MMM yyyy' }}</p>
          </div>
          @if (loanInfo().disbursedAt) {
          <div>
            <p class="text-xs font-semibold text-slate-400">Disbursed At</p>
            <p class="mt-1 text-sm font-semibold text-blue-600">{{ loanInfo().disbursedAt | date:'dd MMM yyyy' }}</p>
          </div>
          }
        </div>
      </div>
    </div>

    <!-- Narration -->
    @if (loanInfo().narration) {
    <div>
      <div class="flex items-center gap-2 mb-3">
        <div class="w-1 h-4 bg-primary-800 rounded-full"></div>
        <h3 class="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">Loan Purpose</h3>
      </div>
      <div class="bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-700/50 rounded-lg p-5">
        <p class="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{{ loanInfo().narration }}</p>
      </div>
    </div>
    }

    <!-- Counter Offer -->
    @if (loanInfo().counterOffer) {
    <div>
      <div class="flex items-center gap-2 mb-3">
        <div class="w-1 h-4 bg-amber-500 rounded-full"></div>
        <h3 class="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">Counter Offer</h3>
        <span class="text-[10px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-600 border border-amber-200 uppercase tracking-widest">
          {{ loanInfo().counterOffer!.type }}
        </span>
      </div>

      <!-- Side by side: Original vs Counter -->
      <div class="grid grid-cols-2 gap-4">
        <!-- Original -->
        <div class="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <p class="text-xs font-semibold text-slate-400 mb-3">Original Request</p>
          <div class="space-y-3">
            <div>
              <p class="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Amount</p>
              <p class="mt-0.5 text-sm font-bold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().principal_amount || loanInfo().amount) | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Receivable Amount</p>
              <p class="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().receivableAmount || 0) | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Tenure</p>
              <p class="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanInfo().tenure || '—' }} {{ getTenureUnit(loanInfo().product?.tenureType, loanInfo().tenure) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Total Interest</p>
              <p class="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().interest || 0) | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Total Repayment</p>
              <p class="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().totalRepayment || 0) | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Processing Fee</p>
              <p class="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">GHS {{ (loanInfo().processingFee || loanInfo().product?.processingFee || 0) | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-slate-400 uppercase tracking-widest">Repayment Date</p>
              <p class="mt-0.5 text-sm font-semibold text-slate-800 dark:text-slate-100">{{ loanInfo().repaymentDate | date:'dd MMM yyyy' }}</p>
            </div>
          </div>
        </div>

        <!-- Counter Offer -->
        <div class="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 rounded-lg p-4">
          <p class="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-widest mb-3">Counter Offer</p>
          <div class="space-y-3">
            <div>
              <p class="text-[10px] font-medium text-amber-500 uppercase tracking-widest">Amount</p>
              <p class="mt-0.5 text-sm font-bold text-amber-900">GHS {{ loanInfo().counterOffer!.amount | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-amber-500 uppercase tracking-widest">Receivable Amount</p>
              <p class="mt-0.5 text-sm font-semibold text-amber-900">GHS {{ (loanInfo().counterOffer!.receivableAmount || 0) | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-amber-500 uppercase tracking-widest">Tenure</p>
              <p class="mt-0.5 text-sm font-semibold text-amber-900">{{ loanInfo().counterOffer!.tenure || '—' }} {{ getTenureUnit(loanInfo().product?.tenureType, loanInfo().counterOffer!.tenure) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-amber-500 uppercase tracking-widest">Total Interest</p>
              <p class="mt-0.5 text-sm font-semibold text-amber-900">GHS {{ (loanInfo().counterOffer!.interest || 0) | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-amber-500 uppercase tracking-widest">Total Repayment</p>
              <p class="mt-0.5 text-sm font-semibold text-amber-900">GHS {{ loanInfo().counterOffer!.totalRepayment | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-amber-500 uppercase tracking-widest">Processing Fee</p>
              <p class="mt-0.5 text-sm font-semibold text-amber-900">GHS {{ (loanInfo().counterOffer!.processingFee || 0) | number:'1.2-2' }}</p>
            </div>
            <div>
              <p class="text-[10px] font-medium text-amber-500 uppercase tracking-widest">Repayment Date</p>
              <p class="mt-0.5 text-sm font-semibold text-amber-900">{{ loanInfo().counterOffer!.repaymentDate | date:'dd MMM yyyy' }}</p>
            </div>
          </div>
        </div>
      </div>

      <p class="text-[10px] text-slate-400 mt-2">Counter submitted on {{ loanInfo().counterOffer!.createdAt | date:'dd MMM yyyy, HH:mm' }}</p>
    </div>
    }

    <!-- Documents -->
    @if (loanInfo().documents && loanInfo().documents!.length > 0) {
    <div>
      <div class="flex items-center gap-2 mb-3">
        <div class="w-1 h-4 bg-primary-800 rounded-full"></div>
        <h3 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">Documents</h3>
        <span class="text-[10px] font-semibold text-slate-400 bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-full">{{ loanInfo().documents!.length }}</span>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        @for (doc of loanInfo().documents!; track doc; let i = $index) {
        <ng-container *ngTemplateOutlet="docCard; context: { doc: doc, index: i + 1 }"></ng-container>
        }
      </div>
    </div>
    }

    <!-- User Documents -->
    @if (loanInfo().user?.documents && loanInfo().user!.documents!.length > 0) {
    <div>
      <div class="flex items-center gap-2 mb-3">
        <div class="w-1 h-4 bg-primary-800 rounded-full"></div>
        <h3 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">Applicant Documents</h3>
        <span class="text-[10px] font-semibold text-slate-400 bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-full">{{ loanInfo().user!.documents!.length }}</span>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        @for (doc of loanInfo().user!.documents!; track doc; let i = $index) {
        <ng-container *ngTemplateOutlet="docCard; context: { doc: doc, index: i + 1 }"></ng-container>
        }
      </div>
    </div>
    }

    <!-- Guarantors -->
    @if (groupedGuarantors.length > 0) {
    <div>
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center gap-2">
          <div class="w-1 h-4 bg-primary-800 rounded-full"></div>
          <h3 class="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-widest">Guarantors</h3>
          <span class="text-[10px] font-semibold text-slate-400 bg-slate-100 dark:bg-slate-900 px-2 py-0.5 rounded-full">{{ groupedGuarantors.length }}</span>
        </div>
        <button (click)="showDiscrepancies = !showDiscrepancies"
          class="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-lg border transition-colors"
          [ngClass]="showDiscrepancies
            ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-900'
            : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
          </svg>
          {{ showDiscrepancies ? 'Hide discrepancies' : 'Show discrepancies' }}
        </button>
      </div>
      <div class="space-y-6">
        @for (group of groupedGuarantors; track group.token; let i = $index) {
        @let discrepancies = getDiscrepancies(group);
        @let activeDiscrepancies = getActiveDiscrepancies(group);
        <div class="border rounded-xl overflow-hidden"
          [ngClass]="activeDiscrepancies.length > 0 ? 'border-rose-200 dark:border-rose-900' : 'border-slate-200 dark:border-slate-700'">

          <!-- Card header -->
          <div class="px-6 py-4 flex items-center justify-between border-b"
            [ngClass]="activeDiscrepancies.length > 0 ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-900' : 'bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700'">
            <div class="flex items-center gap-4">
              <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                [ngClass]="activeDiscrepancies.length > 0 ? 'bg-rose-100 dark:bg-rose-900' : 'bg-primary-100 dark:bg-primary-900'">
                <span class="text-base font-bold"
                  [ngClass]="activeDiscrepancies.length > 0 ? 'text-rose-700 dark:text-rose-300' : 'text-primary-700 dark:text-primary-300'">
                  {{ group.applicant.name.charAt(0).toUpperCase() }}
                </span>
              </div>
              <div>
                <p class="text-sm font-bold text-slate-800 dark:text-slate-100">{{ group.applicant.name }}</p>
                <p class="text-xs text-slate-400 capitalize mt-0.5">{{ group.applicant.relationship }}</p>
              </div>
            </div>
            <div class="flex items-center gap-2 flex-wrap justify-end">
              @if (activeDiscrepancies.length > 0) {
                <span class="text-[10px] font-bold px-3 py-1 rounded-full bg-rose-100 text-rose-700 border border-rose-200 uppercase tracking-wide flex items-center gap-1.5">
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
                  {{ discrepancies.length }} discrepanc{{ discrepancies.length === 1 ? 'y' : 'ies' }}

                </span>
              }
              @if (group.guarantor) {
                @if (discrepancies.length === 0) {
                  <span class="text-[10px] font-bold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 uppercase tracking-wide flex items-center gap-1.5">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/></svg>
                    All matched
                  </span>
                }
              } @else {
                <span class="text-[10px] font-bold px-3 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 uppercase tracking-wide flex items-center gap-1.5">
                  <span class="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block animate-pulse"></span>
                  Pending
                </span>
              }
              <button (click)="sendSms(group.token, loanInfo().id!, group.applicant.name); $event.stopPropagation()"
                [disabled]="smsSendingTokens.has(group.token)"
                class="inline-flex items-center gap-1.5 text-[10px] font-bold px-3 py-1 rounded-full border uppercase tracking-wide transition-all disabled:opacity-50 disabled:cursor-not-allowed bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border-primary-200 dark:border-primary-800 hover:bg-primary-100 dark:hover:bg-primary-900/50">
                @if (smsSendingTokens.has(group.token)) {
                  <svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                  </svg>
                  Sending...
                } @else {
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3v-3z"/>
                  </svg>
                  Resend SMS
                }
              </button>
              <span class="text-[10px] font-bold px-2.5 py-1 rounded-full bg-primary-50 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 border border-primary-100 dark:border-primary-800">G{{ i + 1 }}</span>
            </div>
          </div>

          <!-- Two sections: applicant-submitted | guarantor-submitted -->
          <div class="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 dark:divide-slate-700">

            <!-- Left: submitted by borrower -->
            <div class="p-6">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Submitted by applicant</p>
              <div class="space-y-4">

                <div class="rounded-lg px-3 py-2.5 -mx-3"
                  [ngClass]="activeDiscrepancies.includes('name') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                  <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('name') ? 'text-rose-400' : 'text-slate-400'">Name</p>
                  <p class="text-sm font-semibold" [ngClass]="activeDiscrepancies.includes('name') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.applicant.name }}</p>
                </div>

                <div class="rounded-lg px-3 py-2.5 -mx-3"
                  [ngClass]="activeDiscrepancies.includes('phone') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                  <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('phone') ? 'text-rose-400' : 'text-slate-400'">Phone</p>
                  <p class="text-sm font-semibold font-mono" [ngClass]="activeDiscrepancies.includes('phone') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.applicant.phone }}</p>
                </div>

                <div class="rounded-lg px-3 py-2.5 -mx-3"
                  [ngClass]="activeDiscrepancies.includes('digitalAddress') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                  <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('digitalAddress') ? 'text-rose-400' : 'text-slate-400'">Digital Address</p>
                  <p class="text-sm font-semibold" [ngClass]="activeDiscrepancies.includes('digitalAddress') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.applicant.digitalAddress || '—' }}</p>
                </div>

                <div class="rounded-lg px-3 py-2.5 -mx-3"
                  [ngClass]="activeDiscrepancies.includes('workPlace') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                  <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('workPlace') ? 'text-rose-400' : 'text-slate-400'">Place of Work</p>
                  <p class="text-sm font-semibold" [ngClass]="activeDiscrepancies.includes('workPlace') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.applicant.workPlace || '—' }}</p>
                </div>

                <div class="rounded-lg px-3 py-2.5 -mx-3"
                  [ngClass]="activeDiscrepancies.includes('workRole') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                  <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('workRole') ? 'text-rose-400' : 'text-slate-400'">Job Detail (Role / Position)</p>
                  <p class="text-sm font-semibold" [ngClass]="activeDiscrepancies.includes('workRole') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.applicant.workRole || '—' }}</p>
                </div>

                <div class="rounded-lg px-3 py-2.5 -mx-3"
                  [ngClass]="activeDiscrepancies.includes('relationship') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                  <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('relationship') ? 'text-rose-400' : 'text-slate-400'">Relationship</p>
                  <p class="text-sm font-semibold capitalize" [ngClass]="activeDiscrepancies.includes('relationship') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.applicant.relationship || '—' }}</p>
                </div>

              </div>

            </div>

            <!-- Right: submitted by guarantor -->
            <div class="p-6">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">Submitted by guarantor</p>
              @if (group.guarantor) {
                <div class="space-y-4 mb-6">

                  <div class="rounded-lg px-3 py-2.5 -mx-3"
                    [ngClass]="activeDiscrepancies.includes('name') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                    <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('name') ? 'text-rose-400' : 'text-slate-400'">Name</p>
                    <p class="text-sm font-semibold" [ngClass]="activeDiscrepancies.includes('name') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.guarantor.name }}</p>
                  </div>

                  <div class="rounded-lg px-3 py-2.5 -mx-3"
                    [ngClass]="activeDiscrepancies.includes('phone') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                    <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('phone') ? 'text-rose-400' : 'text-slate-400'">Phone</p>
                    <p class="text-sm font-semibold font-mono" [ngClass]="activeDiscrepancies.includes('phone') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.guarantor.phone }}</p>
                  </div>

                  <div class="rounded-lg px-3 py-2.5 -mx-3"
                    [ngClass]="activeDiscrepancies.includes('ghanaCardNumber') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                    <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('ghanaCardNumber') ? 'text-rose-400' : 'text-slate-400'">Ghana Card</p>
                    <p class="text-sm font-semibold font-mono" [ngClass]="activeDiscrepancies.includes('ghanaCardNumber') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.guarantor.ghanaCardNumber }}</p>
                  </div>

                  <div class="rounded-lg px-3 py-2.5 -mx-3"
                    [ngClass]="activeDiscrepancies.includes('digitalAddress') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                    <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('digitalAddress') ? 'text-rose-400' : 'text-slate-400'">Digital Address</p>
                    <p class="text-sm font-semibold" [ngClass]="activeDiscrepancies.includes('digitalAddress') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.guarantor.digitalAddress }}</p>
                  </div>

                  <div class="rounded-lg px-3 py-2.5 -mx-3"
                    [ngClass]="activeDiscrepancies.includes('workRole') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                    <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('workRole') ? 'text-rose-400' : 'text-slate-400'">Work Role</p>
                    <p class="text-sm font-semibold" [ngClass]="activeDiscrepancies.includes('workRole') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.guarantor.workRole }}</p>
                  </div>

                  <div class="rounded-lg px-3 py-2.5 -mx-3"
                    [ngClass]="activeDiscrepancies.includes('netSalary') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                    <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('netSalary') ? 'text-rose-400' : 'text-slate-400'">Net Salary</p>
                    <p class="text-sm font-semibold" [ngClass]="activeDiscrepancies.includes('netSalary') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">GHS {{ group.guarantor.netSalary | number:'1.2-2' }}</p>
                  </div>

                  <div class="rounded-lg px-3 py-2.5 -mx-3"
                    [ngClass]="activeDiscrepancies.includes('relationship') ? 'bg-rose-50 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-900' : ''">
                    <p class="text-xs mb-1" [ngClass]="activeDiscrepancies.includes('relationship') ? 'text-rose-400' : 'text-slate-400'">Relationship</p>
                    <p class="text-sm font-semibold capitalize" [ngClass]="activeDiscrepancies.includes('relationship') ? 'text-rose-700 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'">{{ group.guarantor.relationship || '—' }}</p>
                  </div>

                </div>
                <div class="grid grid-cols-2 gap-3">
                  @if (group.guarantor.ghanaCardImage) {
                  <ng-container *ngTemplateOutlet="docCard; context: { doc: group.guarantor.ghanaCardImage, index: 'Ghana Card' }"></ng-container>
                  }
                  @if (group.guarantor.image) {
                  <ng-container *ngTemplateOutlet="docCard; context: { doc: group.guarantor.image, index: 'Selfie' }"></ng-container>
                  }
                  @if (group.guarantor.signature) {
                  <ng-container *ngTemplateOutlet="docCard; context: { doc: group.guarantor.signature, index: 'Signature' }"></ng-container>
                  }
                </div>
              } @else {
                <div class="flex flex-col items-center justify-center py-10 text-center">
                  <div class="relative flex items-center justify-center mb-4 w-10 h-10">
                    <span class="w-5 h-5 rounded-full bg-amber-400 inline-block animate-pulse z-10"></span>
                    <span class="absolute w-10 h-10 rounded-full bg-amber-400 animate-ping opacity-40"></span>
                    <span class="absolute w-7 h-7 rounded-full bg-amber-300 animate-ping opacity-50" style="animation-delay: 0.3s;"></span>
                  </div>
                  <p class="text-sm font-semibold text-amber-500 animate-pulse">Awaiting guarantor response</p>
                  <p class="text-xs text-slate-300 dark:text-slate-500 mt-1">An SMS has been sent to the guarantor</p>
                </div>
              }
            </div>

          </div>
        </div>
        }
      </div>
    </div>
    }

    <!-- Shared doc card template -->
    <ng-template #docCard let-doc="doc" let-index="index">
      <div class="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden group">
        <!-- Thumbnail -->
        <div class="relative w-full h-28 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-center cursor-pointer"
          (click)="openPreview(doc)">
          @if (isImage(doc)) {
          <img [src]="doc" alt="Document {{ index }}" class="w-full h-full object-cover" />
          } @else {
          <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          }
          <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </div>
        </div>
        <!-- Footer -->
        <div class="flex items-center justify-between px-3 py-2">
          <div class="min-w-0">
            <p class="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">Document {{ index }}</p>
            <p class="text-[10px] text-slate-400">{{ docExtension(doc) | uppercase }}</p>
          </div>
          <div class="flex items-center gap-1 flex-shrink-0">
            <button (click)="openPreview(doc)" title="Preview"
              class="p-1.5 rounded hover:bg-slate-200 dark:bg-slate-700 text-slate-400 hover:text-slate-700 dark:text-slate-300 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
            <button (click)="downloadFile(doc, index)" title="Download"
              class="p-1.5 rounded hover:bg-slate-200 dark:bg-slate-700 text-slate-400 hover:text-primary-800 dark:text-primary-400 transition-colors">
              <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </ng-template>

  </div>
</div>

<!-- Document Preview Modal -->
@if (previewUrl) {
<div class="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
  (click)="closePreview()">
  <div class="relative max-w-4xl w-full max-h-[90vh] flex flex-col" (click)="$event.stopPropagation()">
    <!-- Modal Header -->
    <div class="flex items-center justify-between bg-slate-900 px-4 py-3 rounded-t-lg">
      <p class="text-xs font-semibold text-slate-300 truncate">{{ docExtension(previewUrl) | uppercase }} Document</p>
      <div class="flex items-center gap-2 flex-shrink-0">
        <button (click)="downloadFile(previewUrl, 0)"
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-primary-800 rounded hover:opacity-90 transition-opacity">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download
        </button>
        <button (click)="closePreview()" class="p-1.5 rounded text-slate-400 hover:text-white transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
    <!-- Modal Body -->
    <div class="bg-white dark:bg-slate-800 rounded-b-lg overflow-auto flex-1 flex items-center justify-center min-h-[300px]">
      @if (isImage(previewUrl)) {
      <img [src]="previewUrl" alt="Preview" class="max-w-full max-h-[80vh] object-contain" />
      } @else if (isPdf(previewUrl)) {
      <iframe [src]="safePreviewUrl" class="w-full h-[80vh] border-0"></iframe>
      } @else {
      <div class="flex flex-col items-center justify-center py-16 text-center px-6">
        <svg class="w-12 h-12 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">Preview not available</p>
        <p class="text-xs text-slate-400 mb-4">This file type cannot be previewed in the browser.</p>
        <button (click)="downloadFile(previewUrl, 0)"
          class="px-4 py-2 text-xs font-semibold text-white bg-primary-800 rounded hover:opacity-90 transition-opacity">
          Download File
        </button>
      </div>
      }
    </div>
  </div>
</div>
}